/**
 * Runtime feature flags — server-owned switches the app reads at startup.
 *
 * Distinct from `src/config/featureFlags.ts`, which reads `REACT_APP_FEATURE_*`
 * baked into the bundle at build time. These come from `GET /api/config/features`
 * so an operator can flip one by editing the API's .env and restarting it — no
 * web rebuild, no redeploy (#1149).
 *
 * Offline behaviour is deliberate: the last fetched value is cached and reused,
 * and a client that has never reached the endpoint assumes ENABLED. This app is
 * offline-first, so hiding a feature because the network is down would be the
 * wrong default — and it is safe, because the server enforces the same flag
 * independently and answers a disabled call with 503 TEXT_INTAKE_DISABLED.
 */
import { REACT_APP_API_TOKEN } from '@env';

import { getApiConfig } from '../config/api';
import { getAuthService } from './auth/authServiceFactory';
import { storage } from './storage';

const STORAGE_KEY = 'cached_runtime_feature_flags';

export interface RuntimeFeatureFlags {
  /** Paste-text and .txt import. See server `isTextIntakeEnabled()`. */
  textIntake: boolean;
  /**
   * The development-only "Every minute" digest cadence. See server
   * `isDigestMinuteFrequencyAllowed()`.
   *
   * ⚠️ THE ONLY FLAG HERE THAT DEFAULTS TO FALSE, and deliberately against the
   * grain of the two rules above it. Text intake defaults permissive because
   * hiding a feature when the network is down would be wrong in an
   * offline-first app, and the server rejects independently. This one is the
   * opposite: it offers a cadence that exists for developers, the server
   * refuses it anyway, and an unreachable endpoint must not put it in front of
   * a real user.
   */
  digestMinuteFrequency: boolean;
}

/** Assumed when nothing has ever been fetched — see the offline note above. */
export const DEFAULT_RUNTIME_FEATURE_FLAGS: RuntimeFeatureFlags = {
  textIntake: true,
  digestMinuteFrequency: false,
};

function coerce(raw: unknown): RuntimeFeatureFlags {
  const record = (raw ?? {}) as Record<string, unknown>;
  return {
    // Only an explicit `false` disables; a malformed or partial payload falls
    // back to the permissive default rather than blanking the UI.
    textIntake: record.textIntake === false ? false : true,
    // …and only an explicit `true` enables this one (see the interface).
    digestMinuteFrequency: record.digestMinuteFrequency === true,
  };
}

class RuntimeFeatureFlagsService {
  private inFlight: Promise<RuntimeFeatureFlags> | null = null;
  private memo: RuntimeFeatureFlags | null = null;

  /**
   * The flags for this app session. Hits the network once and then answers
   * from memory: `useButtonPermissions` calls this on every stage/request
   * change across five call sites, and a request each time would be noise.
   *
   * The cost is that flipping the switch server-side reaches an already-open
   * client on its next reload, not immediately — acceptable for a kill switch
   * because the server rejects the call regardless of what the UI shows.
   */
  async get(): Promise<RuntimeFeatureFlags> {
    if (this.memo) {
      return this.memo;
    }
    return this.refresh();
  }

  /**
   * The in-memory value, without touching the network or storage — null until
   * the first successful fetch this session. For synchronous first-paint
   * callers (`useButtonPermissions`) that must not wait on `get()`.
   */
  peek(): RuntimeFeatureFlags | null {
    return this.memo;
  }

  /** Last value persisted by a successful fetch, or null if there is none. */
  async getCached(): Promise<RuntimeFeatureFlags | null> {
    try {
      const cached = await storage.getJSON<RuntimeFeatureFlags>(STORAGE_KEY);
      return cached ? coerce(cached) : null;
    } catch {
      return null;
    }
  }

  /** Drops the in-memory value so the next `get()` re-fetches. For tests. */
  reset(): void {
    this.memo = null;
    this.inFlight = null;
  }

  /**
   * Fetch from the API, caching on success. On any failure returns the cached
   * value, else the permissive default — never throws, because a flag lookup
   * must not be able to break app startup.
   */
  async refresh(): Promise<RuntimeFeatureFlags> {
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = (async () => {
      try {
        const token = await getAuthService().getToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        if (REACT_APP_API_TOKEN) {
          headers['x-api-token'] = REACT_APP_API_TOKEN;
        }
        const response = await fetch(
          `${getApiConfig().BASE_URL}/api/config/features`,
          { method: 'GET', headers, credentials: 'include' },
        );
        if (!response.ok) {
          throw new Error(`features endpoint responded ${response.status}`);
        }
        const flags = coerce(await response.json());
        this.memo = flags;
        await storage.setJSON(STORAGE_KEY, flags);
        return flags;
      } catch {
        // Deliberately NOT memoized: a transient failure (server restarting,
        // brief offline) should self-heal on the next call rather than pin the
        // fallback for the rest of the session.
        return (await this.getCached()) ?? DEFAULT_RUNTIME_FEATURE_FLAGS;
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }
}

export const runtimeFeatureFlagsService = new RuntimeFeatureFlagsService();
